import type { GetPrimeXQuoteDetailsResponse } from '../../../../../../../api/quotes.api.types';
import type {
  CarrierEligibility,
  GetCarrierEligibilityResponse,
} from '../../../../../../../api/quotes.api';
import type { CoverageEndorsementsState } from '../../../../../policies/prime-x/endorsements/types';
import type { QuoteActions } from '../../../../../primeX-underwriter/components/footer/constants';
import type { QuotesList } from '../../../../../primeX-underwriter/reducer/types';
import type {
  RateModifier,
  RaterXCoverage,
} from '../../../api/types/rater-x-preset';
import type { PrimeXQuoteState, RaterOptionKeysType } from '../../../types';
import type { AmendatoryCoverageManager } from '../components/coverages/amendatory-coverages/manager';
import type { createActionHandlers } from '../reducer';

export interface UnderwriteQuoteState extends PrimeXQuoteState {
  carrierPrefix?: string;
  treatyId?: string;
  isRenewal: boolean;
  quote: GetPrimeXQuoteDetailsResponse;
  readOnly: boolean;
  quoteList: QuotesList[];
  carriers: GetCarrierEligibilityResponse;
  raterOptions: RaterOptions;
  categoryCustomizations: CategoryCustomizations;
  amendatoryCoverageManager: AmendatoryCoverageManager;
  totalPremium: number;
  uwActionBlockersRef: React.MutableRefObject<Map<string, UwActionBlockers>>;
  isAPI?: boolean;
}

export type PrimeXUnderwriteQuoteHandlers = ReturnType<
  typeof createActionHandlers
>;

export type CategoryCustomizations = Partial<{
  [K in CategoryCustomizationKeys]: {
    [key: string]: { select: boolean };
  };
}>;

export type CategoryCustomizationKeys =
  | 'rateModifierCategoriesCustomizations'
  | 'coverageCategoriesCustomizations';

type RaterOptions = {
  [K in RaterOptionKeysType]: {
    options: CarrierEligibility[];
    value: string | number;
  };
};

export const isUnderwriterContext = (
  context: any
): context is {
  state: UnderwriteQuoteState;
  handlers: PrimeXUnderwriteQuoteHandlers;
} => {
  return 'quote' in context.state && 'readOnly' in context.state;
};

export const isCoverageEndorsementsState = (
  state: any
): state is CoverageEndorsementsState => {
  return 'policy' in state && 'payload' in state;
};

/**
 * ISO 4217 code off a PrimeX quote-details payload.
 *
 * `GetPrimeXQuoteDetailsResponse` documents `currency` on both the wrapper and the nested `quote`
 * object depending on which the deployed backend actually stamps (MS-268) — read both rather than
 * assume one and silently drop a currency the API did send.
 *
 * Exported separately from {@link getUnderwriteQuoteCurrency} because not every caller holds an
 * `UnderwriteQuoteState`: the non-rater-X underwriter view has the details payload directly. Both
 * go through here so the wrapper-or-nested rule lives in exactly one place.
 */
export const getQuoteDetailsCurrency = (
  quoteDetails:
    | { currency?: string; quote?: { currency?: string } }
    | undefined
    | null
): string | undefined =>
  quoteDetails?.currency ?? quoteDetails?.quote?.currency;

/**
 * ISO 4217 code for the quote being underwritten, when NOT in the MTE/coverage-endorsements
 * flow (that flow reads `context.state.policy.currency` instead, via `isCoverageEndorsementsState`).
 */
export const getUnderwriteQuoteCurrency = (
  state: UnderwriteQuoteState
): string | undefined => getQuoteDetailsCurrency(state.quote);
export type UwActionBlockers = (RaterXCoverage | RateModifier) & {
  actionToBlock: QuoteActions[];
};

export enum ReferralStrategyVariant {
  SINGLE = 'single',
  CATEGORY_COVERAGE = 'category-coverage',
  CATEGORY_RATING_OPTION = 'category-ratingOption',
}

export enum ReferralStrategyForEdit {
  NONE = 'NONE',
  INVALID_CHANGE = 'INVALID_CHANGE',
  SELECTION_CHANGE = 'SELECTION_CHANGE',
  ANY_CHANGE = 'ANY_CHANGE',
}

export const REFERRAL_STRATEGY_OPTIONS: Array<{
  value: ReferralStrategyForEdit;
  label: string;
}> = [
  { value: ReferralStrategyForEdit.NONE, label: 'None' },
  { value: ReferralStrategyForEdit.INVALID_CHANGE, label: 'Invalid Change' },
  {
    value: ReferralStrategyForEdit.SELECTION_CHANGE,
    label: 'Selection Change',
  },
  { value: ReferralStrategyForEdit.ANY_CHANGE, label: 'Any Change' },
];
